import { css } from '@mui/system'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

type Screens = {
    [key in Breakpoint]: number
}

const screens: Screens = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    xxl: 1920,
}

const getWidth = () => {
    return window?.innerWidth || 0
}

const getHeight = () => {
    return window?.innerHeight || 0
}

export const useBreakpoints = () => {
    const [width, setWidth] = useState(getWidth)
    const [height, setHeight] = useState(getHeight)

    const currentBreakpoint = useMemo(() => {
        return Object.keys(screens).reduce(
            (acc, key) => (width >= screens[key as Breakpoint] ? key : acc),
            'sm'
        ) as Breakpoint
    }, [width])

    const aboveBreakpoint = useCallback(
        (breakpoint: Breakpoint) => {
            return width >= screens[breakpoint]
        },
        [width]
    )

    const belowBreakpoint = useCallback(
        (breakpoint: Breakpoint) => {
            return width < screens[breakpoint]
        },
        [width]
    )

    const track = useCallback(() => {
        setWidth(getWidth)
        setHeight(getHeight)
    }, [])

    useEffect(() => {
        window.addEventListener('resize', track)
        return () => window.removeEventListener('resize', track)
    }, [track])

    return {
        currentBreakpoint,
        aboveBreakpoint,
        belowBreakpoint,
        height,
        width,
    }
}

export const media = {
    sm: (...args: any) => css`
        @media (max-width: ${screens.sm}px) {
            ${css(...args)};
        }
    `,
    smAbove: (...args: any) => css`
        @media (min-width: ${screens.sm}px) {
            ${css(...args)};
        }
    `,
    md: (...args: any) => css`
        @media (max-width: ${screens.md}px) {
            ${css(...args)};
        }
    `,
    lg: (...args: any) => css`
        @media (max-width: ${screens.lg}px) {
            ${css(...args)};
        }
    `,
    xl: (...args: any) => css`
        @media (max-width: ${screens.xl}px) {
            ${css(...args)};
        }
    `,
    xxl: (...args: any) => css`
        @media (max-width: ${screens.xxl}px) {
            ${css(...args)};
        }
    `,
}
